import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import Seo from "../../../shared/ui/Seo";
import Modal from "../../../shared/ui/Modal";
import ShippingFeatures from "../../../shared/layout/ShippingFeatures";
import Footer from "../../../shared/layout/Footer";
import BottomFooter from "../../../shared/layout/BottomFooter";
import ProductGrid from "../../products/components/ProductGrid";
import { fetchProductsByCategories } from "../../products/api";
import { fetchProductsByCollectionId } from "../../collections/api";
import { getMaskedBillingPhone, trackOrderByVerifying, trackOrderByCustomerTrackingId } from "../api";
import ComplaintSubmitModal from "./ComplaintSubmitModal";
import { AddressCard, SummaryRow, OrderItemsCard, hasValue } from "./OrderInfo";
import { CURRENCY, featuredCollectionId } from "../../../shared/utils/constants";
import "../styles/my-orders.css";
import "../styles/order-details.css";
import "../styles/track-order.css";

const PAGE_SIZE = 30;

// Unique category ids of the order's products, for the related products row.
const categoryIdsOf = (order) => [
  ...new Set((order?.products || []).flatMap((p) => (p.productCategories || []).map((c) => c.id))),
];

// The tracking API returns flat billing/shipping fields; AddressCard takes one address object.
const addressFrom = (order, prefix) => ({
  firstName: order[`${prefix}FirstName`],
  lastName: order[`${prefix}LastName`],
  address1: order[`${prefix}Address1`],
  city: order[`${prefix}City`],
  state: order[`${prefix}State`],
  postCode: order[`${prefix}Postcode`],
  country: order[`${prefix}Country`],
  phone: order[`${prefix}Phone`],
  email: order[`${prefix}Email`],
});

const OrderTracking = () => {
  const location = useLocation();
  const customerTrackingId = new URLSearchParams(location.search).get("customerTrackingId");

  const [trackingNumber, setTrackingNumber] = useState("");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Phone verification before the order is shown
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [maskedPhone, setMaskedPhone] = useState(null);
  const [lastFour, setLastFour] = useState("");
  const [verifying, setVerifying] = useState(false);

  const [complaintOpen, setComplaintOpen] = useState(false);
  const [products, setProducts] = useState([]);

  const showOrder = (data) => {
    setOrder(data);
    const ids = categoryIdsOf(data);
    if (ids.length) {
      fetchProductsByCategories(ids, 0, PAGE_SIZE)
        .then((res) => setProducts(res?.content || res?.publicProducts?.content || []))
        .catch((err) => console.error("Error fetching products:", err));
    }
  };

  // Before anything is tracked, suggest products from the featured collection.
  useEffect(() => {
    if (customerTrackingId) return;
    fetchProductsByCollectionId(featuredCollectionId, 0, PAGE_SIZE)
      .then((data) => setProducts(data?.publicProducts?.content || []))
      .catch((err) => console.error("Error fetching collection products:", err));
  }, [customerTrackingId]);

  // A tracking link (?customerTrackingId=…) opens the order directly, no phone check needed.
  useEffect(() => {
    if (!customerTrackingId) return;
    setIsLoading(true);
    trackOrderByCustomerTrackingId(customerTrackingId)
      .then(showOrder)
      .catch((err) => {
        console.error("Error fetching tracked order:", err);
        setError(err.response?.data?.message || "We couldn't find an order for this link.");
      })
      .finally(() => setIsLoading(false));
  }, [customerTrackingId]);

  const handleTrack = async (event) => {
    event.preventDefault();
    if (!trackingNumber.trim()) {
      setError("Please enter a valid tracking number.");
      return;
    }
    setIsLoading(true);
    setError(null);
    setOrder(null);
    try {
      const phoneResponse = await getMaskedBillingPhone(trackingNumber.trim());
      setMaskedPhone(phoneResponse.billingPhone);
      setLastFour("");
      setShowPhoneModal(true);
    } catch (err) {
      console.error("Error fetching order details:", err);
      setError(err.response?.data?.message || "Order not found or an error occurred.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (event) => {
    event.preventDefault();
    if (lastFour.length !== 4) {
      toast.error("Enter last four digits.");
      return;
    }
    setVerifying(true);
    try {
      showOrder(await trackOrderByVerifying(trackingNumber.trim(), lastFour));
      setShowPhoneModal(false);
      setLastFour("");
    } catch (err) {
      toast.error(err.response?.data?.message || "Verification Failed");
    } finally {
      setVerifying(false);
    }
  };

  const currency = order?.currency || CURRENCY;

  return (
    <>
      <Seo title="Track Order" noindex />
      <section className="track-page">
        <div className="container container-lg">
          <div className="track-hero">
            <span className="track-hero__icon" aria-hidden="true">
              <i className="ph ph-package" />
            </span>
            <h1 className="track-hero__title">Track your order</h1>
            <p className="track-hero__text">
              Enter the tracking number from your order confirmation. To raise a complaint, track the order first.
            </p>
            <form className="track-form" onSubmit={handleTrack}>
              <label className="visually-hidden" htmlFor="tracking-number">Tracking number</label>
              <div className="track-form__field">
                <i className="ph ph-hash" aria-hidden="true" />
                <input
                  id="tracking-number"
                  type="text"
                  placeholder="Tracking number"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  autoComplete="off"
                  enterKeyHint="search"
                />
              </div>
              <button type="submit" className="ui-btn ui-btn--primary track-form__submit" disabled={isLoading}>
                {isLoading ? (
                  <i className="ph ph-spinner ui-btn__spin" />
                ) : (
                  <i className="ph ph-magnifying-glass" />
                )}
                Track
              </button>
            </form>
            {error && (
              <p className="ui-note ui-note--error track-hero__error" role="alert">
                <i className="ph ph-warning-circle" aria-hidden="true" />
                {error}
              </p>
            )}
          </div>

          {order && (
            <div className="track-result">
              <header className="od-head">
                <div className="od-head__info">
                  <div className="od-head__title-row">
                    <h2 className="od-head__title">Order #{order.orderNumber}</h2>
                    {hasValue(order.status) && <span className="order-status">{order.status}</span>}
                  </div>
                  {hasValue(order.createdAt) && (
                    <p className="od-head__meta">Placed on {new Date(order.createdAt).toLocaleString()}</p>
                  )}
                </div>
                {!order.complaint && (
                  <div className="od-head__actions">
                    <button type="button" className="ui-btn ui-btn--ghost ui-btn--sm" onClick={() => setComplaintOpen(true)}>
                      <i className="ph ph-chat-circle-text" />
                      Report a problem
                    </button>
                  </div>
                )}
              </header>

              <div className="od-grid">
                <div className="od-col">
                  <OrderItemsCard
                    currency={currency}
                    items={(order.products || []).map((p, index) => ({
                      key: p.productId || index,
                      name: p.productName,
                      image: p.productImages?.[0],
                      quantity: p.quantity,
                      price: p.price,
                    }))}
                  />
                  {order.complaint && (
                    <section className="od-card">
                      <div className="od-card__title-row">
                        <h2 className="od-card__title">Your complaint</h2>
                        {hasValue(order.complaint.status) && (
                          <span className="order-status">{order.complaint.status}</span>
                        )}
                      </div>
                      <p className="od-text">{order.complaint.content}</p>
                    </section>
                  )}
                  {hasValue(order.customerNote) && (
                    <section className="od-card">
                      <h2 className="od-card__title">Order note</h2>
                      <p className="od-text">{order.customerNote}</p>
                    </section>
                  )}
                </div>

                <div className="od-col">
                  <section className="od-card">
                    <h2 className="od-card__title">Payment summary</h2>
                    <div className="od-summary">
                      {hasValue(order.paymentMethod) && (
                        <div className="od-summary__row">
                          <span>Payment method</span>
                          <span className="od-summary__method">{order.paymentMethod}</span>
                        </div>
                      )}
                      <SummaryRow label="Subtotal" value={order.totalAmount} currency={currency} />
                      <SummaryRow label="Delivery fee" value={order.deliveryFee} currency={currency} />
                      <SummaryRow label="Discount" value={order.orderDiscount} currency={currency} />
                      <SummaryRow label="Tax" value={order.orderTax} currency={currency} />
                      <SummaryRow label="Total" value={order.netAmount} currency={currency} strong />
                    </div>
                  </section>
                  <AddressCard title="Shipping address" address={addressFrom(order, "shipping")} />
                  <AddressCard title="Billing address" address={addressFrom(order, "billing")} />
                </div>
              </div>
            </div>
          )}

          {products.length > 0 && (
            <div className="track-related">
              <ProductGrid products={products} showLoadmore={false} title={order ? "Related products" : "You may like"} />
            </div>
          )}
        </div>
      </section>

      <Modal
        isOpen={showPhoneModal}
        onClose={() => setShowPhoneModal(false)}
        size="sm"
        title="Order verification"
        subtitle="Enter the last four digits of your phone number."
        dismissible={!verifying}
        footer={
          <button type="submit" form="order-verify-form" className="ui-btn ui-btn--primary ui-btn--block" disabled={verifying}>
            {verifying ? "Verifying…" : "Verify"}
          </button>
        }
      >
        <form id="order-verify-form" className="ui-form" onSubmit={handleVerify}>
          {maskedPhone && (
            <p className="ui-note">
              <i className="ph ph-phone" aria-hidden="true" />
              <span>Registered phone: <strong>{maskedPhone}</strong></span>
            </p>
          )}
          <div className="ui-field">
            <label className="ui-label" htmlFor="order-verify-last-four">Last four digits</label>
            <input
              id="order-verify-last-four"
              type="text"
              inputMode="numeric"
              maxLength={4}
              className="ui-input"
              placeholder="Last four digits"
              value={lastFour}
              onChange={(e) => setLastFour(e.target.value.replace(/\D/g, ""))}
              autoComplete="off"
            />
          </div>
        </form>
      </Modal>

      {order && (
        <ComplaintSubmitModal
          orderNumber={order.orderNumber}
          isOpen={complaintOpen}
          onClose={() => setComplaintOpen(false)}
          onSubmitted={(content) => setOrder((current) => ({ ...current, complaint: { content } }))}
        />
      )}

      <ShippingFeatures />
      <Footer />
      <BottomFooter />
    </>
  );
};

export default OrderTracking;
